import React, { useEffect, useState } from 'react';
import { Mail, Clock, CheckCircle, XCircle, Search, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

interface EmailForward {
  id: string;
  from_email: string;
  from_name: string | null;
  to_email: string;
  subject: string;
  text_body: string | null;
  html_body: string | null;
  forwarding_status: string;
  error_message: string | null;
  attachment_count: number;
  received_at: string;
  forwarded_at: string | null;
}

const EmailDashboard: React.FC = () => {
  const [emails, setEmails] = useState<EmailForward[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedEmail, setSelectedEmail] = useState<EmailForward | null>(null);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('email_forwards')
        .select('*')
        .order('received_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('forwarding_status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setEmails(data || []);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [statusFilter]);

  const filteredEmails = emails.filter(email => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      email.from_email.toLowerCase().includes(term) ||
      email.subject.toLowerCase().includes(term) ||
      (email.from_name && email.from_name.toLowerCase().includes(term))
    );
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'forwarded':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'forwarded':
        return 'bg-green-500/20 text-green-300';
      case 'failed':
        return 'bg-red-500/20 text-red-300';
      default:
        return 'bg-yellow-500/20 text-yellow-300';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const navigateHome = () => {
    window.history.pushState({}, '', '/');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-900 text-white p-8">
      <Toaster position="top-center" />

      <nav className="max-w-7xl mx-auto mb-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Email Dashboard</h1>
          <button
            onClick={navigateHome}
            className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by sender or subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-white placeholder-gray-400"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-white"
              >
                <option value="all">All Status</option>
                <option value="forwarded">Forwarded</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>

              <button
                onClick={fetchEmails}
                disabled={loading}
                className="px-4 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-500 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-300 mb-4">
            Showing {filteredEmails.length} of {emails.length} emails
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
              <p className="mt-4 text-gray-300">Loading emails...</p>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-300 text-lg">No emails found</p>
              <p className="text-gray-400 text-sm mt-2">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Emails will appear here once received'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEmails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => setSelectedEmail(email)}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-4 cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="mt-1">{getStatusIcon(email.forwarding_status)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-white truncate">
                            {email.from_name || email.from_email}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${getStatusColor(email.forwarding_status)}`}>
                            {email.forwarding_status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-400 mb-1 truncate">
                          {email.from_email}
                        </div>
                        <div className="text-white font-medium mb-1 truncate">
                          {email.subject}
                        </div>
                        <div className="text-sm text-gray-400 line-clamp-2">
                          {email.text_body?.substring(0, 150)}...
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm text-gray-300 mb-1">
                        {formatDate(email.received_at)}
                      </div>
                      {email.attachment_count > 0 && (
                        <div className="text-xs text-yellow-400">
                          {email.attachment_count} attachment{email.attachment_count !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  {email.error_message && (
                    <div className="mt-2 text-sm text-red-300 bg-red-500/10 rounded p-2">
                      Error: {email.error_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedEmail && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedEmail(null)}
        >
          <div
            className="bg-gradient-to-br from-blue-600 to-blue-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/20">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  {getStatusIcon(selectedEmail.forwarding_status)}
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedEmail.subject}</h2>
                    <p className="text-sm text-gray-300">
                      From: {selectedEmail.from_name || selectedEmail.from_email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedEmail(null)}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <div className="text-sm text-gray-400 space-y-1">
                <p>To: {selectedEmail.to_email}</p>
                <p>Received: {formatDate(selectedEmail.received_at)}</p>
                {selectedEmail.forwarded_at && (
                  <p>Forwarded: {formatDate(selectedEmail.forwarded_at)}</p>
                )}
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedEmail.html_body ? (
                <div
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedEmail.html_body }}
                />
              ) : (
                <pre className="whitespace-pre-wrap text-white font-sans">
                  {selectedEmail.text_body}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDashboard;
